// Fields after the one open in Customize. The spec is already frozen. Each
// field is read with fetchPresetInputsFor, computed on its own grid, and
// saved only after the server accepts it.
import { ApiError } from "../api";
import {
  assembleFertLayers,
  DEFAULT_CATION_RATIOS,
  withLimeSoilStamp,
  type RecInputs,
} from "./fertility";
import {
  computePresetLayers,
  fetchPresetInputsFor,
  type FertPreset,
  type PresetInputs,
  type PresetLayers,
} from "./fertPreset";
import {
  commitRecSet,
  invalidateRxCoverage,
  type SavedRecSet,
} from "./fertSets";
import { gridStamp } from "./rxGrid";
import {
  optsFor,
  replacementFor,
  withTunables,
  type Base,
  type RxTunables,
} from "./rxInputs";
import {
  FLAT_K2O_BY_CROP,
  FLAT_P2O5_BY_CROP,
  type Replacement,
  type Season,
  type SeasonPlan,
  type YieldMaps,
} from "./replacement";
import { solveCriticalForBudget } from "./budgetSolve";
import { budgetTarget, tunablesAfterBudget } from "./rxBudget";
import { useFertRecs } from "../stores/fertRecsStore";
import { useLayerStore } from "../stores/layerStore";
import type { DashboardField, ManifestLayer } from "../types/contract";

export interface RecSpec {
  preset: FertPreset;
  seasons: Season[];
  name: string;
  tunables: RxTunables | null;
}

export interface FieldResult {
  field: DashboardField;
  ok: boolean;
  error?: string;
  skipped: string[];
}

interface Built {
  ok: boolean;
  error?: string;
  skipped: string[];
  set?: SavedRecSet;
  layers?: ManifestLayer[];
  summary?: NonNullable<PresetLayers["summary"]>;
}

let recSeq = 0;

export function captureRecSpec(
  preset: FertPreset,
  seasons: Season[],
  name: string,
  tunables: RxTunables | null,
): RecSpec {
  return {
    preset: structuredClone(preset),
    seasons: structuredClone(seasons),
    name,
    tunables: tunables ? structuredClone(tunables) : null,
  };
}

export function fieldFailures(done: FieldResult[]): string | null {
  const failed = done.filter((row) => !row.ok);
  if (!failed.length) return null;
  return failed
    .map((row) => `${row.field.field_name} — ${row.error ?? "error"}`)
    .join("; ");
}

export async function runRecBatch(
  fields: DashboardField[],
  spec: RecSpec,
): Promise<FieldResult[]> {
  const frozen = captureRecSpec(
    spec.preset,
    spec.seasons,
    spec.name,
    spec.tunables,
  );
  const done: FieldResult[] = [];
  for (const field of fields) done.push(await runOne(field, frozen));
  // Each accepted set already invalidated coverage. One more pass after the
  // loop picks up the last field if a commit's refetch was still in flight.
  invalidateRxCoverage();
  return done;
}

function failureMessage(e: unknown): string {
  if (e instanceof ApiError || e instanceof Error) return e.message;
  return "Something went wrong.";
}

function recId(fieldId: string): string {
  recSeq += 1;
  return `frs-${fieldId}-${Date.now().toString(36)}-${recSeq}`;
}

function coverageYear(
  nutrient: FertPreset["nutrient"],
  seasons: number,
): number {
  const span = nutrient === "lime" ? 4 : Math.max(1, seasons);
  return new Date().getFullYear() + span - 1;
}

function isAlbrecht(preset: FertPreset): boolean {
  if (preset.nutrient === "k2o") return !!preset.flags.k2oAlbrecht;
  if (preset.nutrient === "lime") return !!preset.flags.limeAlbrecht;
  return false;
}

function hasCredit(soil: RecInputs): boolean {
  return (soil.pMinCredit ?? []).some(
    (v) => v != null && Number.isFinite(v) && v > 0,
  );
}

function hasFeatures(layers: { inline?: unknown }[]): boolean {
  return layers.some((layer) => {
    const inline = layer.inline;
    if (!inline || typeof inline !== "object") return false;
    const features = (inline as { features?: unknown[] }).features;
    return (features?.length ?? 0) > 0;
  });
}

function savedSet(
  fieldId: string,
  spec: RecSpec,
  computed: PresetLayers,
  soil: RecInputs,
  replacement: Replacement | null,
  // cation must match the layers that were drawn: the tuned path draws with optsFor
  // (cation:false, the same as field 1's Customize save); the untuned preset path draws
  // with computed.withCation.
  stamp: { seasonCount: number; cation: boolean },
): SavedRecSet {
  const nutrient = spec.preset.nutrient;
  const { seasonCount, cation } = stamp;
  return {
    id: recId(fieldId),
    name: spec.name.trim() || spec.preset.label,
    createdAt: new Date().toISOString(),
    fieldId,
    ...gridStamp(computed.gridId, computed.cellCount),
    nutrients: [nutrient],
    coversThroughYear: coverageYear(nutrient, seasonCount),
    cation,
    ...(cation
      ? {
          cationCaMg: DEFAULT_CATION_RATIOS.caMg,
          cationMgK: DEFAULT_CATION_RATIOS.mgK,
        }
      : {}),
    sources: computed.sources,
    soil: nutrient === "lime" ? withLimeSoilStamp(soil, nutrient) : soil,
    replacement,
    ...(replacement
      ? { replP: computed.applyReplP, replK: computed.applyReplK }
      : {}),
    kfix: !!soil.kFixEff,
    ...(soil.kFixEff ? { kfixSet: "standard" as const, kfixRelease: 0.5 } : {}),
    pMin: hasCredit(soil),
    ...(soil.k2oAlbrecht ? { albrechtK: true } : {}),
    ...(soil.limeAlbrecht ? { albrechtLime: true } : {}),
    ...(soil.albrechtTargets ? { albrechtTargets: soil.albrechtTargets } : {}),
    rateWindow: "final",
  };
}

// A season keeps the basis the user picked when THIS field has that map. Otherwise it falls back the
// same way resolveSeasons does (machine → simulated → flat preset rate), so a season never silently
// contributes nothing on a field that lacks field 1's yield data.
export function seasonsOnField(
  seasons: SeasonPlan[],
  maps: YieldMaps,
  nutrient: "p2o5" | "k2o",
): SeasonPlan[] {
  const flat = nutrient === "p2o5" ? FLAT_P2O5_BY_CROP : FLAT_K2O_BY_CROP;
  return seasons.map((season) => {
    if (season.basis === "flat") return { ...season };
    if (season.basis === "machine" && maps.machine[season.crop])
      return { ...season };
    if (season.basis === "simulated" && maps.simulated[season.crop])
      return { ...season };
    if (maps.machine[season.crop])
      return { crop: season.crop, basis: "machine" as const };
    if (maps.simulated[season.crop])
      return { crop: season.crop, basis: "simulated" as const };
    return {
      crop: season.crop,
      basis: "flat" as const,
      flatRateLbAc: flat[season.crop] ?? 0,
    };
  });
}

function withFieldBudget(
  soilIn: RecInputs,
  tunables: RxTunables,
  albrecht: boolean,
  nutrient: "p2o5" | "k2o",
  base: Base,
): RxTunables {
  if (tunables.budgetLbAc == null) return tunables;
  // A budget is a per-field target: re-solve the critical on THIS field's soil, exactly as
  // rxEditStore.set does for field 1, instead of copying field 1's solved critical.
  const target = budgetTarget(tunables, nutrient);
  const soilNow = withTunables(
    soilIn,
    { ...tunables, budgetLbAc: null },
    albrecht,
    nutrient,
    base,
  );
  const sol = solveCriticalForBudget(
    {
      soil: soilNow,
      nutrient,
      tierGap: Math.max(0, tunables.kCritHighCec - tunables.kCritLowCec),
    },
    target,
  );
  return tunablesAfterBudget(tunables, nutrient, target, sol);
}

export function tune(
  fieldId: string,
  spec: RecSpec,
  tunables: RxTunables,
  computed: PresetLayers,
): Built {
  const soilIn = computed.soil;
  const nutrient = spec.preset.nutrient;
  if (!soilIn) return { ok: false, error: "error", skipped: computed.skipped };
  const base: Base = {
    soil: soilIn,
    geoms: computed.geoms,
    gridId: computed.gridId,
    cellOrder: "grid",
    cellCount: computed.cellCount,
    sources: computed.sources,
    yieldMaps: computed.yieldMaps,
  };
  const albrecht = isAlbrecht(spec.preset);
  const fieldTunables =
    nutrient === "lime"
      ? tunables
      : withFieldBudget(
          soilIn,
          {
            ...tunables,
            seasons: seasonsOnField(
              tunables.seasons,
              computed.yieldMaps,
              nutrient,
            ),
          },
          albrecht,
          nutrient,
          base,
        );
  const soil = withTunables(soilIn, fieldTunables, albrecht, nutrient, base);
  const built = assembleFertLayers(
    computed.geoms,
    soil,
    optsFor(nutrient, fieldTunables, base),
  );
  if (!hasFeatures(built.layers) || !built.summary) {
    return {
      ok: false,
      error: "The modeled maps had no values on this field’s cells.",
      skipped: computed.skipped,
    };
  }
  const seasons = nutrient === "lime" ? 0 : fieldTunables.seasons.length;
  const replacement =
    nutrient === "lime" ? null : replacementFor(fieldTunables, base);
  return {
    ok: true,
    skipped: computed.skipped,
    layers: built.layers,
    summary: built.summary,
    set: savedSet(fieldId, spec, computed, soil, replacement, {
      seasonCount: seasons,
      cation: false,
    }),
  };
}

async function buildField(inputs: PresetInputs, spec: RecSpec): Promise<Built> {
  const computed = await computePresetLayers(inputs, spec.preset, spec.seasons);
  if (!computed.ok || !computed.soil || !computed.summary) {
    return {
      ok: false,
      error: computed.error ?? "error",
      skipped: computed.skipped,
    };
  }
  if (!spec.tunables) {
    return {
      ok: true,
      skipped: computed.skipped,
      layers: computed.layers,
      summary: computed.summary,
      set: savedSet(
        inputs.field.field_id,
        spec,
        computed,
        computed.soil,
        computed.replacement,
        {
          seasonCount: spec.seasons.length,
          cation: computed.withCation,
        },
      ),
    };
  }
  return tune(inputs.field.field_id, spec, spec.tunables, computed);
}

async function runOne(
  field: DashboardField,
  spec: RecSpec,
): Promise<FieldResult> {
  const fieldId = field.field_id;
  useFertRecs.getState().computing(fieldId);
  try {
    const built = await buildField(await fetchPresetInputsFor(field), spec);
    if (!built.ok || !built.set || !built.layers || !built.summary) {
      const error = built.error ?? "Something went wrong.";
      useFertRecs.getState().fail(fieldId, error);
      return { field, ok: false, error, skipped: built.skipped };
    }
    const savedSets = await commitRecSet(built.set);
    useLayerStore.getState().reseed(built.layers);
    useFertRecs
      .getState()
      .ready(fieldId, built.layers, built.summary, built.set.id, savedSets);
    return { field, ok: true, skipped: built.skipped };
  } catch (e) {
    const error = failureMessage(e);
    useFertRecs.getState().fail(fieldId, error);
    return { field, ok: false, error, skipped: [] };
  }
}
